import { Link } from "react-router-dom";
import { inferAdminModeForRow } from "./admin-event-prefill";
import { useAdminEventsData } from "./admin-events-data-context";

type Row = Record<string, unknown>;

function text(v: unknown): string {
  return String(v ?? "").trim();
}

function fmtDateTime(v: unknown): string {
  const raw = text(v);
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" });
}

function fanPostAdminLabel(r: Row): string {
  if (text(r.fan_group_name)) return "H!P予定管理";
  if (text(r.fan_artist_name)) return "J.P ROOM予定管理";
  return "全ての予定";
}

function fanPostRoute(r: Row): string {
  const mode = inferAdminModeForRow(r);
  return `/admin/events/${mode}?edit=${encodeURIComponent(text(r.id))}`;
}

function FanPostCard({ row }: { row: Row }) {
  const title = text(row.event_name) || text(row.source_title) || "（タイトル未設定）";
  const group = text(row.fan_group_name);
  const artist = text(row.fan_artist_name);
  const detailUrl = text(row.detail_url);
  const deleted = text(row.deleted_at);

  return (
    <article className="card visitor-fan-post-card">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>{title}</h3>
          <p className="meta" style={{ margin: 0, lineHeight: 1.6 }}>
            {text(row.event_date) || "日付未設定"} ・ {text(row.prefecture) || "都道府県未設定"}
            {text(row.performance_count) ? ` ・ ${text(row.performance_count)}公演` : ""}
          </p>
        </div>
        <span className="pill mono">{fanPostAdminLabel(row)}</span>
      </div>

      <div className="meta" style={{ marginTop: 8, lineHeight: 1.65 }}>
        {group ? <div>グループ名: {group}</div> : null}
        {artist ? <div>アーティスト名: {artist}</div> : null}
        {text(row.source_title) ? <div>URL取得タイトル: {text(row.source_title)}</div> : null}
        {text(row.fan_private_note) ? <div>投稿メモ: {text(row.fan_private_note)}</div> : null}
        <div>投稿日時: {fmtDateTime(row.created_at)}</div>
        {deleted ? <div style={{ color: "#8b0000" }}>アーカイブ済み: {fmtDateTime(deleted)}</div> : null}
      </div>

      <div className="row" style={{ marginTop: 10, gap: 8, flexWrap: "wrap" }}>
        <Link className="btn small" to={fanPostRoute(row)}>
          {fanPostAdminLabel(row)}で編集
        </Link>
        {detailUrl ? (
          <a className="btn small secondary" href={detailUrl} target="_blank" rel="noreferrer">
            公式URLを開く
          </a>
        ) : null}
      </div>
    </article>
  );
}

export function VisitorEventRequestsPage() {
  const { rows, loading, fetchError } = useAdminEventsData();
  const fanPosts = rows
    .filter((r) => text(r.source) === "fan_post" && !text(r.deleted_at))
    .sort((a, b) => text(b.created_at).localeCompare(text(a.created_at)));

  const groupCount = fanPosts.filter((r) => text(r.fan_group_name)).length;
  const artistOnlyCount = fanPosts.filter((r) => text(r.fan_artist_name) && !text(r.fan_group_name)).length;

  return (
    <section className="card" style={{ padding: "14px 16px" }}>
      <h2 style={{ margin: "0 0 8px", fontSize: 16 }}>こんなイベントもあるよ！投稿一覧</h2>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--muted)", lineHeight: 1.55 }}>
        表サイトの「こんなイベントもあるよ！」から即公開された予定をすべて表示します。グループ名がある投稿は
        <strong> H!P予定管理</strong>、アーティスト名だけの投稿は<strong> J.P ROOM予定管理</strong>で編集できます。
      </p>

      <div className="row" style={{ gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <span className="pill mono">全{fanPosts.length}件</span>
        <span className="pill mono">H!P振り分け {groupCount}件</span>
        <span className="pill mono">J.P ROOM振り分け {artistOnlyCount}件</span>
      </div>

      {loading ? <p className="meta">読み込み中…</p> : null}
      {fetchError ? <p style={{ color: "#b00020" }}>{fetchError}</p> : null}
      {!loading && fanPosts.length === 0 ? <p className="meta">まだ投稿はありません。</p> : null}

      <div className="visitor-fan-post-list">
        {fanPosts.map((row) => (
          <FanPostCard key={text(row.id)} row={row} />
        ))}
      </div>
    </section>
  );
}
